/**
 * TransformProgressBar — deterministic progress bar for /meetings/:id/transform-stream.
 *
 * Receives a phase label + a 0–100 progress number and renders a pine-filled
 * bar on a cloud track, plus the current phase as caption text. Phase
 * boundaries correspond to real SSE events from the streaming transform
 * endpoint:
 *
 *   creating     0 →  15  (POST /meetings responded; draft row exists)
 *   analyzing   15 →  25  (glossary + project memory loaded; Claude request sent)
 *   writing     25 →  90  (Claude tool_use JSON streaming in; fills by delta chars)
 *   validating  90 →  98  (stream ended; server validating schema + persisting)
 *   done        98 → 100  (response delivered; about to navigate)
 *
 * Client interpolates smoothly between phase anchors via CSS transition on
 * the bar's width so the user sees continuous motion even when events
 * arrive in bursts.
 */
import type { CSSProperties } from 'react';

export type TransformPhase =
  | 'creating'
  | 'analyzing'
  | 'writing'
  | 'validating'
  | 'done';

const PHASE_LABELS: Record<TransformPhase, string> = {
  creating: 'Creating draft…',
  analyzing: 'Analyzing your notes…',
  writing: 'Claude is writing the minutes…',
  validating: 'Validating structure…',
  done: 'Done',
};

/**
 * Expected tool_use JSON byte ceiling used to interpolate the
 * writing-phase progress. Real payloads are typically 4–8 KB; we aim
 * higher so the bar tops out just short of 90% on the long tail and
 * snaps to 92 on validating. Tuned together with the bands below.
 */
const EXPECTED_OUTPUT_BYTES = 12_000;

/** Base progress per phase. Writing is a range, not a constant. */
export function phaseBaseProgress(phase: TransformPhase): number {
  switch (phase) {
    case 'creating':
      return 0;
    case 'analyzing':
      return 15;
    case 'writing':
      return 25;
    case 'validating':
      return 92;
    case 'done':
      return 100;
  }
}

/**
 * Map the cumulative byte count emitted during 'writing' into the
 * 25–90 band. Ease-out so motion slows near the end (matches what a
 * long Claude response actually feels like — bursty at start,
 * trailing off).
 */
export function writingProgressFromBytes(bytes: number): number {
  const frac = Math.min(1, bytes / EXPECTED_OUTPUT_BYTES);
  const eased = 1 - Math.pow(1 - frac, 1.6);
  return 25 + eased * 65;
}

export function TransformProgressBar({
  phase,
  progress,
  style,
}: {
  phase: TransformPhase;
  progress: number;
  style?: CSSProperties;
}) {
  const clamped = Math.max(0, Math.min(100, Math.round(progress)));
  return (
    <div
      className="rounded-[20px] border border-charcoal/15 bg-cloud/60 px-6 py-5 shadow-sm"
      style={style}
    >
      <div className="flex items-baseline justify-between gap-4">
        <div className="text-sm font-semibold text-pine">
          {PHASE_LABELS[phase]}
        </div>
        <div className="font-mono text-xs tabular-nums text-charcoal/70">
          {clamped}%
        </div>
      </div>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-charcoal/10">
        <div
          className="h-full rounded-full bg-pine transition-[width] duration-500 ease-out"
          style={{ width: `${clamped}%` }}
        />
      </div>
      <div className="mt-3 text-[11px] text-charcoal/60">
        Claude turns your notes into OJB-formatted minutes in ~10–20 seconds.
        The editor opens automatically when it's done.
      </div>
    </div>
  );
}
